import React from 'react';
import type { BuilderViewport, FormListStatus } from '../../forms/types';
import { formatRelativeSavedAgo } from '../../forms/utils';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { FormsStatusPill } from './FormsStatusPill';
import {
  FormBuilderDesktopIcon,
  FormBuilderMobileIcon,
  FormBuilderTabletIcon,
} from './FormBuilderViewportIcons';

const VIEWPORTS: {
  key: BuilderViewport;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}[] = [
  { key: 'desktop', label: 'desktop', Icon: FormBuilderDesktopIcon },
  { key: 'tablet', label: 'tablet', Icon: FormBuilderTabletIcon },
  { key: 'mobile', label: 'mobile', Icon: FormBuilderMobileIcon },
];

export interface FormBuilderToolbarProps {
  formName: string;
  editingName: boolean;
  onEditingNameChange: (editing: boolean) => void;
  onFormNameChange: (name: string) => void;
  viewport: BuilderViewport;
  onViewportChange: (viewport: BuilderViewport) => void;
  blockCount: number;
  showAutosave?: boolean;
  status?: FormListStatus;
  lastSavedAt?: Date | null;
  isSaving?: boolean;
  isPublishing?: boolean;
  canPersist?: boolean;
  onPreview?: () => void;
  onSave?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
}

export function FormBuilderToolbar({
  formName,
  editingName,
  onEditingNameChange,
  onFormNameChange,
  viewport,
  onViewportChange,
  blockCount,
  showAutosave = true,
  status = 'draft',
  lastSavedAt = null,
  isSaving = false,
  isPublishing = false,
  canPersist = true,
  onPreview,
  onSave,
  onPublish,
  onUnpublish,
}: FormBuilderToolbarProps) {
  const isPublished = status === 'published';
  const persistDisabled = !canPersist || isSaving || isPublishing;
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!lastSavedAt) return;
    const id = window.setInterval(() => {
      setTick((n) => n + 1);
    }, 30_000);
    return () => {
      window.clearInterval(id);
    };
  }, [lastSavedAt]);

  const savedLabel = isSaving
    ? 'Saving…'
    : lastSavedAt
      ? `auto-saved ${formatRelativeSavedAgo(lastSavedAt)}`
      : 'not saved yet';

  const blockLabel = blockCount === 1 ? '1 block' : `${blockCount} blocks`;
  const showActions = onPreview ?? onSave ?? onPublish ?? onUnpublish;
  return (
    <header className="fm-builder-toolbar z-[6] flex shrink-0 items-center border-b border-[var(--cream-2)]">
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {editingName ? (
          <input
            autoFocus
            value={formName}
            onChange={(e) => {
              onFormNameChange(e.target.value);
            }}
            onBlur={() => {
              onEditingNameChange(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onEditingNameChange(false);
            }}
            className="min-w-[280px] rounded-lg border border-[var(--ai-3)] bg-[var(--cream-1)] px-2.5 py-1.5 font-[var(--font-display)] text-[16px] font-semibold tracking-[-0.01em] text-[var(--ink-0)] outline-none focus:shadow-[0_0_0_3px_var(--ai-tint)]"
          />
        ) : (
          <button
            type="button"
            title="Click to rename"
            onClick={() => {
              onEditingNameChange(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-[var(--font-display)] text-[16px] font-semibold tracking-[-0.01em] text-[var(--ink-0)] transition-colors hover:bg-[var(--cream-0)]"
          >
            {formName}
            <Icon kind="pencil" size={13} stroke={1.8} className="text-[var(--ink-4)]" />
          </button>
        )}
        <FormsStatusPill status={status} />
        {showAutosave ? (
          <span className="ml-0.5 inline-flex items-center gap-1.5 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-4)]">
            <span className="elva-live" aria-hidden />
            {blockLabel} · {savedLabel}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {showActions ? (
          <div className="flex items-center gap-2">
            {onPreview ? (
              <Button variant="secondary" size="sm" iconLeft="eye" onClick={onPreview}>
                Preview
              </Button>
            ) : null}
            {onSave ? (
              <Button
                variant={isPublished ? 'primary' : 'secondary'}
                size="sm"
                iconLeft="check"
                disabled={persistDisabled}
                onClick={onSave}
              >
                {isSaving ? 'Saving…' : isPublished ? 'Save' : 'Save draft'}
              </Button>
            ) : null}
            {isPublished && onUnpublish ? (
              <Button variant="secondary" size="sm" disabled={persistDisabled} onClick={onUnpublish}>
                {isPublishing ? 'Updating…' : 'Unpublish'}
              </Button>
            ) : null}
            {!isPublished && onPublish ? (
              <Button
                variant="primary"
                size="sm"
                iconLeft="send"
                disabled={persistDisabled}
                onClick={onPublish}
              >
                {isPublishing ? 'Publishing…' : 'Publish'}
              </Button>
            ) : null}
          </div>
        ) : null}
        <div className="flex gap-0.5 rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-0)] p-0.5">
        {VIEWPORTS.map((v) => (
          <button
            key={v.key}
            type="button"
            title={v.label}
            onClick={() => {
              onViewportChange(v.key);
            }}
            className={cn(
              'flex size-[28px] w-[34px] items-center justify-center rounded-[7px] border-0 transition-all',
              viewport === v.key
                ? 'bg-[var(--cream-1)] text-[var(--ink-0)] shadow-[var(--sh-1)]'
                : 'bg-transparent text-[var(--ink-3)]',
            )}
          >
            <v.Icon />
          </button>
        ))}
        </div>
      </div>
    </header>
  );
}
